import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/data/content";

const links = [
  { href: "#services", label: "Services" },
  { href: "#why", label: "Why Us" },
  { href: "#process", label: "Process" },
  { href: "#gallery", label: "Gallery" },
  { href: "#reviews", label: "Reviews" },
  { href: "#area", label: "Service Area" },
  { href: "#faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="Broady Home Services"
            className="h-11 w-11 object-contain"
          />
          <span className="flex flex-col leading-tight">
            <span className="font-serif-display text-xl md:text-[22px] tracking-tight text-[#111] group-hover:text-[#C8A44D] transition-colors">
              Broady Home Services
            </span>
            <span className="hidden sm:inline text-[9px] tracking-[0.32em] uppercase text-[#666]">
              Las Vegas Valley
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-[#222] hover:text-[#C8A44D] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="nav-call-btn"
            className="hidden md:inline-flex items-center gap-2 text-sm text-[#111] hover:text-[#C8A44D] transition-colors"
          >
            <Phone size={15} strokeWidth={1.5} />
            {BRAND.phone}
          </a>
          <a
            href="#estimate"
            data-testid="nav-estimate-btn"
            className="hidden md:inline-flex btn-gold px-6 py-3 rounded-full text-sm font-medium"
          >
            Get Estimate
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-[#111]"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-white border-b border-black/5 ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mnav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-base text-[#111] py-1"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#estimate"
            onClick={() => setOpen(false)}
            data-testid="mnav-estimate-btn"
            className="btn-gold mt-3 px-6 py-3 rounded-full text-sm font-medium text-center"
          >
            Get a Free Estimate
          </a>
        </nav>
      </div>
    </header>
  );
}
